// export default LoginPage;

import { RootState } from "../../redux/store/store"; // Adjust the import according to your file structure
import React, { useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import { ReactComponent as InteLift } from "../../assets/svg/InteLift.svg";
import Loader from "../common/Loader";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { appVersion } from "../../utils/version";
import backgroundImage from "../../assets/img/background.png";
import { useKeycloak, useAuthSafe } from '../ConditionalAuthProvider';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isKeycloakEnabled } from '../../config/authConfig';

// const appVersion = '2.1.31'; // Define the version of your app

const LoginPage: React.FC = () => {
  const { keycloak } = useKeycloak();
  const { login: backendLogin } = useAuthSafe();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const defaultTheme = createTheme();
  const navigate = useNavigate(); // useNavigate hook to redirect user after successful login
  const loginStatus = useSelector((state: RootState) => state.user.status); // Select the status from user slice
  const { t } = useTranslation();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (!username || !password) {
      setError("Please provide both username and password.");
      return;
    }
    
    if (isKeycloakEnabled()) {
      // Keycloak mode - redirect to Keycloak login
      if (keycloak) {
        keycloak.login();
      }
    } else {
      // Backend authentication mode - use provider login so context updates immediately
      try {
        const ok = await backendLogin(username, password);
        if (ok) {
          navigate('/dashboard');
        } else {
          setError('Invalid credentials');
        }
      } catch (error: any) {
        setError(error.message || 'Login failed');
      }
    }
  };

  const isRejected = loginStatus === "failed";
  const loading = loginStatus === "loading";
  
  // Don't show the form if Keycloak is enabled and user is already authenticated
  if (isKeycloakEnabled() && keycloak?.authenticated) {
    return null; // Keycloak will handle the redirect
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box
        sx={{
          backgroundColor: "#f0f0f0",
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "100vh", // Adjust height as needed
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              backgroundColor: "white",
              backgroundSize: "cover",
              backgroundPosition: "center",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)", // Box shadow
              borderRadius: "10px", // Border radius
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <InteLift style={{ width: "70%", height: "auto" }} />
            <br />
            {error && (
              <Typography
                variant="body2"
                color="error"
                sx={{ marginBottom: 1 }}
              >
                {error}
              </Typography>
            )}
            {isRejected && (
              <Typography
                variant="body2"
                color="error"
                sx={{ marginBottom: 1 }}
              >
                {"Invalid Credentials"}
              </Typography>
            )}
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t("Username")}
                name="email"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t("Password")}
                type="password"
                id="password"
                autoComplete="current-password"
                value={password} // set the value to the password state variable
                onChange={(e) => setPassword(e.target.value)} // update the state variable when the value changes
              />
              {loading ? (
                <Loader />
              ) : (
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  style={{ backgroundColor: "#c00", color: "white" }}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {t("login")}
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              Version {appVersion}
            </Typography>
          </Box>
        </Container>
      </Box>
            {/* ----- WEBSOCKET TESTER CARD -----
            <div className="grid custom-grid-col" style={{ marginTop: "2rem" }}>
        <div className="col-12 sm:col-12 md:col-6 lg:col-4 xl:col-4">
          <Card className="dashboard-card" title="WebSocket Tester">
            <WebSocketTester />
          </Card>
        </div>
      </div> */}
    </ThemeProvider>
  );
};

export default LoginPage;
